import React from 'react';
import hotToast, { Toaster, toast as rawToast } from 'react-hot-toast';

// Compatibility layer over react-hot-toast exposing react-toast-notifications'
// API (ToastProvider + useToasts().addToast(content, {appearance, autoDismiss,
// autoDismissTimeout}, callback)). react-toast-notifications is abandoned and
// its react-transition-group calls findDOMNode, which React 19 removed.

const APPEARANCES = ['error', 'warning', 'success', 'info', 'none'];

const ToastContent = ({ t, appearance, children }) => (
  <div className={`ui-toast ui-toast--${APPEARANCES.includes(appearance) ? appearance : 'none'}`} role="alert">
    <div className="ui-toast__content">{children}</div>
    <button
      type="button"
      className="ui-toast__dismiss"
      aria-label="Dismiss toast"
      onClick={() => hotToast.dismiss(t.id)}
    >
      <span aria-hidden="true">&times;</span>
    </button>
  </div>
);

/**
 * react-toast-notifications' hook API.
 *
 * addToast(content, options?, callback?) → id
 *   options.appearance: 'error'|'warning'|'success'|'info'|'none' (default none)
 *   options.autoDismiss: boolean (default false — sticky until dismissed)
 *   options.autoDismissTimeout: ms (default 5000)
 * removeToast(id, callback?)
 */
export const useToasts = () => {
  const addToast = React.useCallback((content, options = {}, callback) => {
    const { appearance = 'none', autoDismiss = false, autoDismissTimeout = 5000 } = options;
    const id = rawToast.custom(
      (t) => (
        <ToastContent t={t} appearance={appearance}>
          {content}
        </ToastContent>
      ),
      { duration: autoDismiss ? autoDismissTimeout : Infinity }
    );
    if (callback) callback(id);
    return id;
  }, []);

  const removeToast = React.useCallback((id, callback) => {
    hotToast.dismiss(id);
    if (callback) callback(id);
  }, []);

  return { addToast, removeToast };
};

// HOC form for class components: injects a toastManager prop with
// add / update / remove (react-toast-notifications' manager API).
export const withToastManager = (Component) => {
  const Wrapped = (props) => {
    const { addToast, removeToast } = useToasts();
    const toastManager = React.useMemo(
      () => ({
        add: addToast,
        remove: removeToast,
        update: (id, options = {}) => {
          const { content, appearance = 'none', autoDismiss = false, autoDismissTimeout = 5000 } = options;
          rawToast.custom(
            (t) => (
              <ToastContent t={t} appearance={appearance}>
                {content}
              </ToastContent>
            ),
            { id, duration: autoDismiss ? autoDismissTimeout : Infinity }
          );
        },
      }),
      [addToast, removeToast]
    );
    return <Component {...props} toastManager={toastManager} />;
  };
  Wrapped.displayName = `withToastManager(${Component.displayName || Component.name || 'Component'})`;
  return Wrapped;
};

export const ToastProvider = ({ children }) => (
  <React.Fragment>
    {children}
    <Toaster position="top-right" containerClassName="ui-toast-container" gutter={8} />
  </React.Fragment>
);
